// Silent PWA refresh manager. Registers the service worker, polls
// for new builds, and auto-reloads the page the moment the tab
// becomes hidden if a fresh bundle is waiting. Renders nothing.
//
// Why visibility-driven instead of immediate reload:
//   - An immediate reload mid-session would nuke unsaved editor work.
//   - A toast/prompt forces the user to opt in, which they shouldn't
//     have to.
//   - When the tab is hidden the user is, by definition, not looking
//     — reloading is imperceptible. They come back to a fresh version.
//   - If they never leave the tab, they stay on the old version.
//     That's fine: any genuine multi-day session is rare, and a hard
//     reload is one swipe away.
//
// Update polling: useRegisterSW only checks at registration time, so
// we ping registration.update() on a long interval to keep a stale
// tab catching up with deploys.

import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// 30 minutes — frequent enough that a long-lived tab notices a deploy
// within half an hour, infrequent enough not to burn network on idle
// sessions.
const UPDATE_POLL_MS = 30 * 60 * 1000;

export function PwaUpdater() {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration;
    },
    onRegisterError(error) {
      // Non-fatal — the app still works without offline support.
      // eslint-disable-next-line no-console
      console.warn('Service worker registration failed:', error);
    },
  });

  // Periodic update check for long-lived tabs.
  useEffect(() => {
    const id = window.setInterval(() => {
      registrationRef.current?.update().catch(() => {
        // Update fetch can fail offline — harmless, retry next tick.
      });
    }, UPDATE_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  // When a fresh bundle is waiting AND the tab is hidden, reload.
  // Combined with autoUpdate's skipWaiting, this is a single round
  // trip: the SW already serves the fresh assets, we just need the
  // document to re-fetch index.html.
  useEffect(() => {
    if (!needRefresh) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        updateServiceWorker(true);
      }
    };
    // Prime: if the tab is already hidden when needRefresh flips
    // (e.g. user backgrounded mid-update-check), reload immediately.
    if (document.visibilityState === 'hidden') {
      updateServiceWorker(true);
      return;
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [needRefresh, updateServiceWorker]);

  return null;
}
